import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { usePartnerAuth } from '../../context/PartnerAuthContext';
import { usePartnerBooking } from '../../context/PartnerBookingContext';
import { formatCardDate, displayDeck, cruiseLabel } from '../../components/shared/helpers';
import ModalShell from '../../components/shared/ModalShell';

// Booking-hold countdown banner, sourced from the PARTNER booking context. The
// shared HoldTimer reads the customer context, so it can't be reused here.
// (Duplicated from PartnerCruiseSummary to keep the partner flow isolated.)
function PartnerHoldTimer({ holdSeconds }) {
  const mm = String(Math.floor(holdSeconds / 60)).padStart(2, '0');
  const ss = String(holdSeconds % 60).padStart(2, '0');
  const urgent = holdSeconds > 0 && holdSeconds < 300;

  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-[#D4A843] bg-[rgba(201,168,76,0.1)] px-5 py-4 mb-4 shrink-0">
      <div className="flex flex-col">
        <span className="font-dm-sans text-[13px] text-white/60">Booking held for</span>
        {holdSeconds > 0 ? (
          <span className={`font-cormorant text-3xl leading-none mt-1 tabular-nums ${urgent ? 'text-[#E5484D]' : 'text-[#C9A84C]'}`}>{mm}:{ss}</span>
        ) : (
          <span className="font-dm-sans text-[13px] font-medium text-[#C9A84C] mt-1.5">Your booking hold has expired</span>
        )}
      </div>
      <span className="font-dm-sans text-sm text-white font-medium text-right max-w-[160px] leading-relaxed">
        Complete the booking before time runs out
      </span>
    </div>
  );
}

export default function PartnerPassengerDetails() {
  const { partnerSession, partnerProfile, loadingAuth } = usePartnerAuth();
  const {
    selectedDate, passengers, selectedCruise,
    paxDetails, setPaxDetails,
    agreedTnC, setAgreedTnC,
    holdId, holdSeconds,
    fading, go, exitFlow, exitToDashboard,
  } = usePartnerBooking();

  // Active hold on this step → confirm before releasing + exiting to dashboard.
  const [showExitWarning, setShowExitWarning] = useState(false);


  // Dinner Cruise requires a food preference for every non-infant guest.
  const isDinner = selectedCruise?.cruiseName === 'Dinner Cruise';

  // Passenger summary string (zero counts hidden)
  const paxSummary = [
    `${passengers.adults} ${passengers.adults === 1 ? 'Adult' : 'Adults'}`,
    passengers.children > 0 ? `${passengers.children} ${passengers.children === 1 ? 'Child' : 'Children'}` : null,
    passengers.infants > 0 ? `${passengers.infants} ${passengers.infants === 1 ? 'Infant' : 'Infants'}` : null,
  ].filter(Boolean).join(' · ');

  // Passenger field updates + validation
  const updatePax = (index, field, value) => {
    setPaxDetails(prev => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };

  // Age must fall inside the (non-overlapping) band for the passenger's type.
  const AGE_BANDS = {
    infant: { min: 0,  max: 3,    msg: 'Infant must be 3 or under' },
    child:  { min: 4,  max: 12,   msg: 'Child must be 4–12 years' },
    adult:  { min: 13, max: null, msg: 'Adult must be 13 or older' },
  };
  // Inline age-band error for one passenger, or null. An empty age returns null
  // (it's handled as "incomplete" by paxComplete, not shown as a band error).
  const ageError = (p) => {
    const raw = String(p.age).trim();
    if (raw === '') return null;
    const band = AGE_BANDS[p.type];
    if (!band) return null;
    const n = Number(raw);
    if (Number.isNaN(n)) return null;
    if (n < band.min) return band.msg;
    if (band.max !== null && n > band.max) return band.msg;
    return null;
  };
  // Name is valid when non-empty and free of digits. Digits are stripped on
  // input below, so the /\d/ check is belt-and-braces for the gate.
  const nameValid = (p) => p.name.trim() !== '' && !/\d/.test(p.name);

  const paxComplete = paxDetails.length > 0 && paxDetails.every(p => {
    if (!nameValid(p)) return false;                              // name: required, no digits
    if (String(p.age).trim() === '' || ageError(p) !== null) return false; // age: required + in band
    if (p.type === 'infant') return true;
    if (p.gender === '') return false;                           // gender: non-infant
    return isDinner ? p.food !== '' : true;                      // food: Dinner non-infant
  });

  // Continue is gated on complete passenger details AND T&C consent (the checkbox
  // below), mirroring the customer flow's canPay. Terms is no longer a separate
  // step — the terms page remains only as a readable reference via the link.
  const canProceed = paxComplete && agreedTnC;

  // Guards — wait for the auth check, then require an approved partner session
  // and an active hold (with its cruise + date). A refresh clears in-memory state
  // by design, so fall back to the partner availability screen.
  if (loadingAuth) {
    return (
      <div className="w-full max-w-[480px] p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)] card-enter">
        <div className="bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-7 py-8">
          <p className="font-dm-sans text-sm text-white/55 font-light leading-relaxed text-center">
            Loading...
          </p>
        </div>
      </div>
    );
  }
  if (!partnerSession) return <Navigate to="/partner/login" replace />;
  if (partnerProfile?.approval_status !== 'approved') return <Navigate to="/partner" replace />;
  if (!selectedCruise || selectedDate === null || !holdId) return <Navigate to="/partner/book/availability" replace />;

  return (
    <div className={`w-full max-w-[680px] mx-auto px-0 md:px-2 card-enter transition-opacity duration-[250ms] ${fading ? 'opacity-0' : 'opacity-100'}`}>

        {/* Header row: back arrow → cruise summary · Exit to Dashboard */}
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => go('/partner/book/cruise')}
            className="flex items-center justify-center w-8 h-8 rounded-full border border-[rgba(212,168,67,0.3)] hover:border-[rgba(212,168,67,0.65)] hover:bg-[rgba(212,168,67,0.06)] transition-[border-color,background-color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]"
            aria-label="Go back"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M9 2L4 7L9 12" stroke="#D4A843" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {/* Active hold → confirm before releasing + exiting */}
          <button
            type="button"
            onClick={() => setShowExitWarning(true)}
            className="font-dm-sans text-[13px] font-medium text-[rgba(212,168,67,0.6)] hover:text-[#C9A84C] hover:underline underline-offset-2 transition-colors duration-200"
          >
            Exit to Dashboard
          </button>
        </div>

        {/* Booking-hold countdown (continues across the terms step) */}
        <PartnerHoldTimer holdSeconds={holdSeconds} />

        {/* Compact booking summary — non-editable, guest details only (no pricing) */}
        <div className="border border-[rgba(255,255,255,0.12)] rounded-lg bg-[rgba(255,255,255,0.04)] px-4 py-3 mb-6 font-dm-sans text-sm">
          <div className="flex flex-col gap-1 md:flex-row md:items-center md:gap-2.5 text-white/80">
            <span>{formatCardDate(selectedDate)}</span>
            <span className="hidden md:inline text-white/25">·</span>
            <span>{cruiseLabel(selectedCruise, 'short')}&nbsp;· {displayDeck(selectedCruise.cruiseName, selectedCruise.deckLabel)}</span>
            <span className="hidden md:inline text-white/25">·</span>
            <span>{paxSummary}</span>
          </div>
        </div>

        <p className="font-dm-sans text-[12px] uppercase tracking-[0.18em] text-[rgba(201,168,76,0.85)] mb-2">
          Partner Booking
        </p>
        <h2 className="font-cormorant font-normal text-[1.7rem] md:text-[2rem] leading-tight text-white mb-4">
          Passenger Details
        </h2>

        {/* Passenger forms — every name is freely editable (booking for guests) */}
        <div className="flex flex-col gap-4">
          {paxDetails.map((p, idx) => (
            <div key={idx} className="rounded-xl border border-white/[0.09] bg-[#162840] p-3">
              <div className="flex items-center justify-between mb-3">
                <span className="font-dm-sans text-[13px] font-semibold text-[#C9A84C] tracking-[0.03em]">{p.label}</span>
                <span className="font-dm-sans text-[13px] uppercase tracking-[0.13em] font-medium text-white/60 border border-white/15 rounded-full px-2.5 py-1">
                  {p.type === 'adult' ? 'Adult' : p.type === 'child' ? 'Child' : 'Infant'}
                </span>
              </div>

              <div className="flex flex-col gap-2.5">
                {/* Name — editable for every passenger (no locked primary guest) */}
                <div className="group flex items-center gap-3">
                  <label className="w-20 shrink-0 font-dm-sans text-[13px] uppercase tracking-wide font-medium text-[#C9A84C]">Full Name</label>
                  <div className="flex-1 border-b border-white/25 group-focus-within:border-[#C9A84C] pb-1.5 transition-colors duration-200">
                    <input type="text" value={p.name} onChange={e => updatePax(idx, 'name', e.target.value.replace(/\d/g, ''))} className="w-full bg-transparent text-white font-dm-sans text-[15px] outline-none caret-[#D4A843]" />
                  </div>
                </div>

                {/* Age */}
                <div className="group flex items-center gap-3">
                  <label className="w-20 shrink-0 font-dm-sans text-[13px] uppercase tracking-wide font-medium text-[#C9A84C]">Age</label>
                  <div className="flex-1 border-b border-white/25 group-focus-within:border-[#C9A84C] pb-1.5 transition-colors duration-200">
                    <input type="number" min="0" inputMode="numeric" value={p.age} onChange={e => updatePax(idx, 'age', e.target.value.replace(/\D/g, ''))} className="w-full bg-transparent text-white font-dm-sans text-[15px] outline-none caret-[#D4A843]" />
                  </div>
                </div>
                {ageError(p) && (
                  <p className="font-dm-sans text-[13px] text-white/55 font-light leading-relaxed pl-[92px] -mt-1">
                    {ageError(p)}
                  </p>
                )}

                {p.type !== 'infant' && (
                  <>
                    {/* Gender */}
                    <div className="flex items-center gap-3">
                      <label className="w-20 shrink-0 font-dm-sans text-[13px] uppercase tracking-wide font-medium text-[#C9A84C]">Gender</label>
                      <div className="flex-1 flex gap-2">
                        {['Male', 'Female', 'Other'].map(g => (
                          <button
                            key={g}
                            type="button"
                            onClick={() => updatePax(idx, 'gender', g)}
                            className={`flex-1 h-8 rounded-full text-sm font-dm-sans font-medium transition-[background-color,border-color,color] duration-200 ${
                              p.gender === g
                                ? 'bg-[#C9A84C] text-[#0A1628]'
                                : 'bg-transparent border border-white/15 text-white/55 hover:border-white/30'
                            }`}
                          >
                            {g}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Food preference — Dinner Cruise only */}
                    {isDinner && (
                      <div className="flex items-center gap-3">
                        <label className="w-20 shrink-0 font-dm-sans text-[13px] uppercase tracking-wide font-medium text-[#C9A84C] leading-tight">Food Pref</label>
                        <div className="flex-1 flex gap-2">
                          {['Veg', 'Non-Veg', 'Jain'].map(f => (
                            <button
                              key={f}
                              type="button"
                              onClick={() => updatePax(idx, 'food', f)}
                              className={`flex-1 h-8 rounded-full text-sm font-dm-sans font-medium transition-[background-color,border-color,color] duration-200 ${
                                p.food === f
                                  ? 'bg-[#C9A84C] text-[#0A1628]'
                                  : 'bg-transparent border border-white/15 text-white/55 hover:border-white/30'
                              }`}
                            >
                              {f}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {p.type === 'infant' && (
                  <p className="font-dm-sans text-sm text-white/55 font-light">
                    Lap infant — no seat assigned, no charge
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* T&C consent checkbox — the terms page (opened via the link) is now a
            readable reference, not a blocking step. Clicking the link must not
            toggle the checkbox, so it stops propagation. */}
        <button
          type="button"
          onClick={() => setAgreedTnC(v => !v)}
          className="flex items-start gap-3 mt-6 mb-6 text-left w-full"
        >
          <span className={`mt-0.5 w-5 h-5 rounded-[5px] shrink-0 flex items-center justify-center border transition-[background-color,border-color] duration-200 ${
            agreedTnC ? 'bg-[#C9A84C] border-[#C9A84C]' : 'bg-transparent border-white/25'
          }`}>
            {agreedTnC && (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M2.5 6.5L5 9L9.5 3.5" stroke="#0A1628" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </span>
          <span className="font-dm-sans text-sm font-light text-white/65 leading-relaxed">
            I have read and agree to the{' '}
            <span
              role="link"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); go('/partner/book/terms'); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); go('/partner/book/terms'); } }}
              className="text-[#C9A84C] underline underline-offset-2 hover:text-[#B8973E] cursor-pointer"
            >
              Terms &amp; Conditions
            </span>
          </span>
        </button>

        {/* Continue → partner payment (gated on complete details + T&C consent) */}
        <button
          type="button"
          onClick={() => { if (canProceed) go('/partner/book/payment'); }}
          disabled={!canProceed}
          className={`w-full py-[15px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,opacity] duration-[200ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none ${
            canProceed
              ? 'opacity-100 active:scale-[0.98] hover:bg-[#B8973E] focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A1628] cursor-pointer'
              : 'opacity-30 cursor-not-allowed'
          }`}
        >
          Continue to Payment
        </button>

        <button
          type="button"
          onClick={exitFlow}
          className="block mx-auto mt-4 mb-2 font-dm-sans text-[13px] font-medium text-[rgba(212,168,67,0.6)] hover:text-[#C9A84C] hover:underline underline-offset-2 transition-colors duration-200"
        >
          Cancel and return to cruise selection
        </button>

        {/* Exit-to-Dashboard confirm — releases the hold and leaves to /partner */}
        {showExitWarning && (
          <ModalShell
            ariaLabel="Leave booking — the seats are on hold"
            onClose={() => setShowExitWarning(false)}
          >
            {/* Ornamental diamond rule */}
            <div className="flex items-center gap-3 mb-4 w-full max-w-[180px]">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent to-[rgba(212,168,67,0.4)]" />
              <div className="w-1.5 h-1.5 rotate-45 bg-[rgba(212,168,67,0.55)] shrink-0" />
              <div className="flex-1 h-px bg-gradient-to-l from-transparent to-[rgba(212,168,67,0.4)]" />
            </div>

            <h2 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-3">
              Leave booking?
            </h2>
            <p className="font-dm-sans text-sm font-light text-white/60 leading-relaxed mb-6">
              Your held seats will be released and this booking will not be completed. Leave to the dashboard?
            </p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={exitToDashboard}
                className="flex-1 py-[13px] bg-transparent border border-white/20 text-white/70 font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,border-color,color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] hover:border-white/40 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/25 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35]"
              >
                Leave
              </button>
              <button
                type="button"
                onClick={() => setShowExitWarning(false)}
                className="flex-1 py-[13px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] hover:bg-[#B8973E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35]"
              >
                Stay
              </button>
            </div>
          </ModalShell>
        )}

      </div>
  );
}
